import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  RefreshCw, Mail, CreditCard, Users, Clock, AlertTriangle,
  CheckCircle, XCircle, Loader2, Calendar, Search, ChevronDown, ChevronUp,
  Pencil, Check, X, Trash2
} from 'lucide-react';

const RenewalsTab = () => {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sendingNotice, setSendingNotice] = useState(false);
  const [takingPayment, setTakingPayment] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [markPaidModalOpen, setMarkPaidModalOpen] = useState(false);
  const [markPaidNote, setMarkPaidNote] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [auditFilter, setAuditFilter] = useState('all'); // 'all' | 'overdue' | 'due_soon' | 'in_term' | 'no_dates'
  const [auditSearch, setAuditSearch] = useState('');
  const [expandedStudentId, setExpandedStudentId] = useState(null);

  const fetchAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await api.get('/payments/renewals/audit');
      setAuditData(res.data);
    } catch (error) {
      console.error('Failed to fetch renewals audit:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      const errMsg = status
        ? `HTTP ${status} from /api/payments/renewals/audit — ${detail}`
        : `Network error — ${detail}`;
      setAuditError(errMsg);
      toast.error('Failed to load renewals audit — see the panel for details.');
    } finally {
      setAuditLoading(false);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    if (!auditData) await fetchAudit();
    // Scroll the panel into view so the user can't miss it
    setTimeout(() => {
      const el = document.querySelector('[data-testid="renewals-audit-panel"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const [amountOverrides, setAmountOverrides] = useState({});
  const [editingAmountId, setEditingAmountId] = useState(null);
  const [editAmountValue, setEditAmountValue] = useState('');

  const getEffectiveAmount = (r) => {
    const o = amountOverrides[r.id];
    if (o !== undefined && o !== null) return o;
    return r.term_price || r.last_amount || 0;
  };

  const buildOverridesPayload = (ids) => {
    const payload = {};
    ids.forEach((id) => {
      if (amountOverrides[id] !== undefined) payload[id] = amountOverrides[id];
    });
    return payload;
  };

  const fetchRenewals = async () => {
    setLoading(true);
    try {
      const response = await api.get('/payments/renewals/pending');
      setRenewals(response.data.renewals || []);
    } catch (error) {
      console.error('Failed to fetch renewals:', error);
      toast.error('Failed to load renewals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRenewals();
  }, []);

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === renewals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(renewals.map(r => r.id)));
    }
  };

  const handleSendNotice = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setSendingNotice(true);
    try {
      const response = await api.post('/payments/renewals/send-notice', {
        renewal_ids: Array.from(selectedIds),
        amount_overrides: buildOverridesPayload(Array.from(selectedIds)),
      });
      
      if (response.data.sent > 0) {
        toast.success(`48-hour notice sent to ${response.data.sent} parent(s)`);
      }
      if (response.data.failed > 0) {
        toast.warning(`Failed to send to ${response.data.failed} parent(s)`);
      }
      
      fetchRenewals();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to send notices:', error);
      toast.error('Failed to send notices');
    } finally {
      setSendingNotice(false);
    }
  };

  const handleTakePayment = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one student');
      return;
    }

    // Check if all selected have saved cards
    const selectedRenewals = renewals.filter(r => selectedIds.has(r.id));
    const withoutCards = selectedRenewals.filter(r => !r.has_saved_card);
    
    if (withoutCards.length > 0) {
      toast.error(`${withoutCards.length} student(s) don't have saved cards`);
      return;
    }

    setTakingPayment(true);
    try {
      // Pass merged student IDs for dedupe-merged rows so take-payment renews
      // bookings under every underlying student_id (fixes the "still showing
      // in renewals after payment" bug for kids with duplicate child rows).
      const mergedMap = {};
      selectedRenewals.forEach(r => {
        if (r.merged_student_ids && r.merged_student_ids.length > 1) {
          mergedMap[r.id] = r.merged_student_ids;
        }
      });
      const response = await api.post('/payments/renewals/take-payment', {
        renewal_ids: Array.from(selectedIds),
        merged_student_ids_map: mergedMap,
        amount_overrides: buildOverridesPayload(Array.from(selectedIds)),
      });
      const d = response.data || {};
      const successful = d.successful || 0;
      const failed = d.failed || 0;

      if (successful > 0) {
        toast.success(`Successfully charged ${successful} renewal(s) - £${(d.total_charged || 0).toFixed(2)} total`);
      }
      if (failed > 0) {
        // Surface the FIRST specific error to the admin so they're not guessing
        const firstErr = (d.errors && d.errors[0]) || null;
        const reason = firstErr ? `${firstErr.student || firstErr.id || ''}: ${firstErr.error || ''}`.trim() : '';
        toast.error(`Failed: ${failed} payment(s)${reason ? ' — ' + reason : ''}`);
        (d.errors || []).forEach(err => console.error('Payment error:', err));
      }
      if (successful === 0 && failed === 0) {
        // Shouldn't happen, but make sure admin always sees feedback.
        toast.warning('No renewals were processed. Check console for response.');
        console.warn('take-payment empty response:', d);
      }

      fetchRenewals();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to take payments:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to process payments — ${detail}`);
    } finally {
      setTakingPayment(false);
    }
  };

  const handleDismiss = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one student');
      return;
    }
    const names = renewals.filter(r => selectedIds.has(r.id)).map(r => r.student_name).join(', ');
    if (!confirm(`Remove ${selectedIds.size} renewal(s) from this list?\n\n${names}\n\nUse this for old or deleted students that shouldn't be here. It does NOT delete the student or any payment history — it just permanently removes these rows from the renewals list.`)) return;
    setDismissing(true);
    try {
      const mergedMap = {};
      renewals.filter(r => selectedIds.has(r.id)).forEach(r => {
        if (r.merged_student_ids && r.merged_student_ids.length > 1) {
          mergedMap[r.id] = r.merged_student_ids;
        }
      });
      const res = await api.post('/payments/renewals/dismiss', {
        renewal_ids: Array.from(selectedIds),
        merged_student_ids_map: mergedMap,
      });
      const d = res.data || {};
      if (d.dismissed > 0) toast.success(`Removed ${d.dismissed} renewal(s) from the list`);
      if (d.errors?.length) toast.warning(`${d.errors.length} could not be removed — ${d.errors[0].error}`);
      setSelectedIds(new Set());
      fetchRenewals();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to remove renewals');
    } finally {
      setDismissing(false);
    }
  };

  const handleMarkPaid = async () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one student');
      return;
    }
    setMarkingPaid(true);
    try {
      // Pass each renewal's displayed price so the invoice records what
      // the admin actually collected (matches the same £ figure they see).
      const amounts = {};
      const mergedMap = {};
      renewals
        .filter(r => selectedIds.has(r.id))
        .forEach(r => {
          amounts[r.id] = getEffectiveAmount(r);
          if (r.merged_student_ids && r.merged_student_ids.length > 1) {
            mergedMap[r.id] = r.merged_student_ids;
          }
        });

      const res = await api.post('/payments/renewals/mark-paid', {
        renewal_ids: Array.from(selectedIds),
        note: markPaidNote.trim(),
        amounts,
        merged_student_ids_map: mergedMap,
      });
      const d = res.data || {};
      const successful = d.successful || 0;
      const failed = d.failed || 0;
      if (successful > 0) {
        toast.success(`Marked ${successful} renewal(s) as paid — £${(d.total_marked || 0).toFixed(2)} total`);
      }
      if (failed > 0) {
        const firstErr = (d.errors && d.errors[0]) || null;
        const reason = firstErr ? `${firstErr.student || firstErr.id || ''}: ${firstErr.error || ''}`.trim() : '';
        toast.error(`Failed: ${failed} renewal(s)${reason ? ' — ' + reason : ''}`);
      }
      setMarkPaidModalOpen(false);
      setMarkPaidNote('');
      setSelectedIds(new Set());
      fetchRenewals();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to mark paid — ${detail}`);
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleBackfillDates = async () => {
    if (!confirm('This will calculate and set term_end_date for all paid bookings that are missing it. This is needed for renewals to work properly. Continue?')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/payments/renewals/backfill-term-dates');
      if (response.data.updated > 0) {
        toast.success(`Updated ${response.data.updated} booking(s) with term end dates`);
        fetchRenewals();
      } else {
        toast.info('All bookings already have term end dates');
      }
    } catch (error) {
      console.error('Failed to backfill dates:', error);
      toast.error('Failed to update term dates');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const selectedTotal = renewals
    .filter(r => selectedIds.has(r.id))
    .reduce((sum, r) => sum + getEffectiveAmount(r), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Users className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Pending Renewals</p>
                <p className="text-2xl font-bold text-white">{renewals.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CreditCard className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Total Due</p>
                <p className="text-2xl font-bold text-white">
                  £{renewals.reduce((sum, r) => sum + getEffectiveAmount(r), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#111] border-[#333]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Selected</p>
                <p className="text-2xl font-bold text-white">
                  {selectedIds.size} (£{selectedTotal.toFixed(2)})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={selectAll}
                className="border-[#333] text-white hover:bg-white/10"
              >
                {selectedIds.size === renewals.length ? 'Deselect All' : 'Select All'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRenewals}
                className="border-[#333] text-white hover:bg-white/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={openAudit}
                className="border-[#7000FF]/60 text-[#B388FF] hover:bg-[#7000FF]/10"
                data-testid="open-renewals-audit-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Audit (why isn't this student showing?)
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!confirm('Delete any bookings / renewals that reference a student who has already been removed?\n\nThis cleans up "ghost" entries left behind by older deletions. It cannot delete an actual student — only their orphaned records.')) return;
                  try {
                    const res = await api.post('/students/admin/cleanup-orphaned-bookings');
                    const total = res.data?.total_deleted || 0;
                    if (total === 0) {
                      toast.success('No orphaned records found — everything is clean.');
                    } else {
                      const parts = Object.entries(res.data.per_collection || {})
                        .filter(([, n]) => n > 0)
                        .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`);
                      toast.success(`Removed ${total} ghost record${total > 1 ? 's' : ''} (${parts.join(', ')})`);
                    }
                    fetchRenewals();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to clean up orphaned bookings');
                  }
                }}
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                data-testid="cleanup-orphaned-bookings-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Remove ghost renewals
              </Button>

              {renewals.length === 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBackfillDates}
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Fix Missing Dates
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSendNotice}
                disabled={selectedIds.size === 0 || sendingNotice}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {sendingNotice ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Send 48hr Notice ({selectedIds.size})
              </Button>
              
              <Button
                onClick={handleTakePayment}
                disabled={selectedIds.size === 0 || takingPayment}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {takingPayment ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Take Payment ({selectedIds.size})
              </Button>

              <Button
                onClick={() => {
                  if (selectedIds.size === 0) {
                    toast.error('Please select at least one student');
                    return;
                  }
                  setMarkPaidModalOpen(true);
                }}
                disabled={selectedIds.size === 0 || markingPaid}
                variant="outline"
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                data-testid="mark-paid-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as paid (other)
              </Button>

              <Button
                onClick={handleDismiss}
                disabled={selectedIds.size === 0 || dismissing}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="dismiss-renewals-btn"
              >
                {dismissing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Remove from list ({selectedIds.size})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Failed payments banner */}
      {renewals.some(r => r.last_payment_failure) && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/40" data-testid="failed-payments-banner">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-red-300 font-semibold">
              {renewals.filter(r => r.last_payment_failure).length} renewal payment(s) could not be taken
            </p>
            <p className="text-white/60 mt-0.5">
              The reason is shown in red on each affected row below. Fix the issue (e.g. ask the parent to update their card), then retry Take Payment — or use "Mark as paid (other)" / "Remove from list".
            </p>
          </div>
        </div>
      )}

      {/* Renewals List */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader className="border-b border-[#333]">
          <CardTitle className="text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-[#FF007F]" />
            Pending Renewals
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {renewals.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-white/60">No pending renewals</p>
              <p className="text-sm text-white/40 mt-1">All students are up to date!</p>
            </div>
          ) : (
            <div className="divide-y divide-[#333]">
              {renewals.map((renewal) => (
                <div
                  key={renewal.id}
                  className={`p-4 hover:bg-[#1a1a1a] transition-colors ${
                    selectedIds.has(renewal.id) ? 'bg-[#FF007F]/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedIds.has(renewal.id)}
                      onCheckedChange={() => toggleSelection(renewal.id)}
                      className="mt-1"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white">
                          {renewal.student_name}
                        </h3>
                        
                        {renewal.notice_sent && (
                          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                            Notice Sent
                          </Badge>
                        )}
                        
                        {!renewal.has_saved_card && (
                          <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                            No Card
                          </Badge>
                        )}

                        {renewal.last_payment_failure && (
                          <Badge className="bg-red-600/30 text-red-300 border-red-500/50" data-testid={`payment-failed-badge-${renewal.id}`}>
                            <XCircle className="w-3 h-3 mr-1" />
                            Payment Failed
                          </Badge>
                        )}
                        
                        <Badge className="bg-[#333] text-white/70">
                          {renewal.payment_type || '6 Week'}
                        </Badge>
                      </div>
                      
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-white/60">
                          <Mail className="w-4 h-4 text-blue-400" />
                          <span className="truncate">{renewal.guardian_email || 'No email'}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-white/60">
                          <Calendar className="w-4 h-4 text-amber-400" />
                          <span>Term ended: {formatDate(renewal.term_end_date)}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-white/60">
                          <Clock className="w-4 h-4 text-red-400" />
                          <span>{renewal.days_overdue} days overdue</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-white/60 flex-wrap">
                          <span className="text-emerald-400 font-bold">£</span>
                          {editingAmountId === renewal.id ? (
                            <span className="flex items-center gap-1">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editAmountValue}
                                onChange={(e) => setEditAmountValue(e.target.value)}
                                className="w-24 bg-[#1a1a1a] border border-amber-500/50 rounded px-2 py-0.5 text-white text-sm"
                                data-testid={`edit-amount-input-${renewal.id}`}
                                autoFocus
                              />
                              <button
                                onClick={() => {
                                  const v = parseFloat(editAmountValue);
                                  if (isNaN(v) || v < 0) { toast.error('Enter a valid amount'); return; }
                                  setAmountOverrides(prev => ({ ...prev, [renewal.id]: Math.round(v * 100) / 100 }));
                                  setEditingAmountId(null);
                                }}
                                className="text-emerald-400 hover:text-emerald-300"
                                data-testid={`save-amount-${renewal.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setEditingAmountId(null)}
                                className="text-white/40 hover:text-white"
                                data-testid={`cancel-amount-${renewal.id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </span>
                          ) : (
                            <>
                              <span className="font-semibold text-emerald-400">
                                {getEffectiveAmount(renewal).toFixed(2)}
                              </span>
                              <button
                                onClick={() => { setEditingAmountId(renewal.id); setEditAmountValue(String(getEffectiveAmount(renewal))); }}
                                className="text-white/30 hover:text-amber-400"
                                title="Edit amount"
                                data-testid={`edit-amount-btn-${renewal.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              {amountOverrides[renewal.id] !== undefined && (
                                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs font-medium flex items-center gap-1" data-testid={`amount-edited-${renewal.id}`}>
                                  edited (was £{(renewal.term_price || renewal.last_amount || 0).toFixed(2)})
                                  <button
                                    onClick={() => setAmountOverrides(prev => { const n = { ...prev }; delete n[renewal.id]; return n; })}
                                    title="Reset to original"
                                    data-testid={`reset-amount-${renewal.id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              )}
                            </>
                          )}
                          {renewal.sibling_discount_applied && typeof renewal.term_price_before_sibling_discount === 'number' && (
                            <span className="px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300 text-xs font-medium" data-testid={`sibling-discount-${renewal.id}`}>
                              -30% sibling (was £{renewal.term_price_before_sibling_discount.toFixed(2)})
                            </span>
                          )}
                          {renewal.scholarship_applied && typeof renewal.term_price_before_scholarship === 'number' && (
                            <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 text-xs font-medium" data-testid={`scholarship-${renewal.id}`}>
                              -{renewal.student_discount_percent}% scholarship (was £{renewal.term_price_before_scholarship.toFixed(2)})
                            </span>
                          )}
                          {typeof renewal.last_amount === 'number' && typeof renewal.term_price === 'number' && renewal.last_amount !== renewal.term_price && !renewal.sibling_discount_applied && (
                            <span className="text-xs text-white/40">(was £{renewal.last_amount.toFixed(2)} pro-rated)</span>
                          )}
                        </div>
                      </div>
                      
                      {renewal.class_names?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {renewal.class_names.map((cls, idx) => (
                            <span 
                              key={idx}
                              className="px-2 py-0.5 bg-[#FF007F]/20 text-[#FF007F] text-xs rounded-full"
                            >
                              {cls}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {renewal.last_payment_failure && (
                        <div className="mt-2 flex items-start gap-2 p-2.5 rounded bg-red-500/10 border border-red-500/30 text-xs" data-testid={`payment-failed-notice-${renewal.id}`}>
                          <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                          <div>
                            <span className="text-red-300 font-semibold">
                              Payment failed {formatDate(renewal.last_payment_failure.failed_at)}:
                            </span>{' '}
                            <span className="text-red-200/90">{renewal.last_payment_failure.reason}</span>
                          </div>
                        </div>
                      )}

                      {renewal.notice_sent && renewal.notice_sent_at && (
                        <p className="mt-2 text-xs text-amber-400/70">
                          Notice sent: {formatDate(renewal.notice_sent_at)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Renewals Audit Panel */}
      {auditOpen && (
        <Card className="bg-[#0e0e15] border-[#7000FF]/40" data-testid="renewals-audit-panel">
          <CardHeader className="border-b border-[#7000FF]/30 flex flex-row items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-[#B388FF]" />
              Renewals Audit — every student with paid bookings
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAudit}
                className="border-[#333] text-white hover:bg-white/10"
                data-testid="audit-refresh-btn"
              >
                {auditLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAuditOpen(false)}
                className="border-[#333] text-white/60 hover:bg-white/10"
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {auditLoading && !auditData ? (
              <div className="py-12 text-center text-white/60">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading audit…
              </div>
            ) : auditError ? (
              <div className="py-8 px-4 bg-red-900/20 border border-red-500/40 rounded text-sm" data-testid="audit-error">
                <div className="flex items-start gap-2 text-red-300 mb-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">Audit failed to load</div>
                    <div className="text-red-200/80 font-mono text-xs break-all">{auditError}</div>
                  </div>
                </div>
                <div className="text-white/60 text-xs mt-3">
                  Send a screenshot of this message if you need help debugging.
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchAudit}
                  className="mt-3 border-[#333] text-white hover:bg-white/10"
                  data-testid="audit-retry-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-2" />
                  Retry
                </Button>
              </div>
            ) : auditData ? (
              <>
                {/* Summary chips */}
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'all', label: `All (${auditData.total_students_with_paid_bookings})`, color: '#888' },
                    { key: 'overdue', label: `Overdue (${auditData.counts.overdue || 0})`, color: '#FF4757' },
                    { key: 'due_soon', label: `Due soon (${auditData.counts.due_soon || 0})`, color: '#FFA502' },
                    { key: 'in_term', label: `In-term (${auditData.counts.in_term || 0})`, color: '#2ED573' },
                    { key: 'no_dates', label: `No dates (${auditData.counts.no_dates || 0})`, color: '#888' },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => setAuditFilter(f.key)}
                      data-testid={`audit-filter-${f.key}`}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        auditFilter === f.key
                          ? 'bg-white/10 border-white/40 text-white'
                          : 'border-[#333] text-white/70 hover:bg-white/5'
                      }`}
                      style={auditFilter === f.key ? { borderColor: f.color, color: f.color } : {}}
                    >
                      {f.label}
                    </button>
                  ))}
                  <input
                    type="text"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    placeholder="Search by name or email…"
                    className="ml-auto bg-[#0a0a0a] border border-[#333] rounded px-3 py-1.5 text-sm text-white placeholder:text-white/30 w-64"
                    data-testid="audit-search-input"
                  />
                </div>

                {/* Students list */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {auditData.students
                    .filter(s => auditFilter === 'all' || s.status === auditFilter)
                    .filter(s => {
                      const q = auditSearch.trim().toLowerCase();
                      if (!q) return true;
                      return (
                        (s.student_name || '').toLowerCase().includes(q) ||
                        (s.guardian_email || '').toLowerCase().includes(q) ||
                        (s.guardian_name || '').toLowerCase().includes(q)
                      );
                    })
                    .map(s => {
                      const isOpen = expandedStudentId === s.student_id;
                      const statusColor = s.status === 'overdue' ? '#FF4757'
                        : s.status === 'due_soon' ? '#FFA502'
                        : s.status === 'in_term' ? '#2ED573'
                        : '#888';
                      return (
                        <div key={s.student_id} className="border border-[#222] rounded bg-[#0a0a0a]" data-testid={`audit-row-${s.student_id}`}>
                          <button
                            onClick={() => setExpandedStudentId(isOpen ? null : s.student_id)}
                            className="w-full flex items-center justify-between p-3 hover:bg-white/5 text-left"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className="px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap"
                                style={{ background: `${statusColor}22`, color: statusColor }}
                              >
                                {s.status.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className="font-semibold text-white truncate">{s.student_name}</span>
                              <span className="text-xs text-white/40 truncate">{s.guardian_email}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-white/60">
                              <span>{s.booking_count} booking{s.booking_count === 1 ? '' : 's'}</span>
                              <span>{(s.term_end_date || '').slice(0, 10) || '—'}</span>
                              {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          </button>
                          {isOpen && (
                            <div className="border-t border-[#222] p-3 space-y-3 text-xs">
                              <div className="text-white/70">
                                <span className="font-semibold" style={{ color: statusColor }}>Why: </span>
                                {s.status_reason}
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                  <thead className="text-white/40">
                                    <tr>
                                      <th className="p-1 font-medium">Class</th>
                                      <th className="p-1 font-medium">Paid at</th>
                                      <th className="p-1 font-medium">Stored term-end</th>
                                      <th className="p-1 font-medium">Effective end (capped)</th>
                                      <th className="p-1 font-medium">Type</th>
                                      <th className="p-1 font-medium text-right">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-white/70">
                                    {s.bookings.map((b, i) => (
                                      <tr key={i} className="border-t border-[#222]">
                                        <td className="p-1">{b.class_name || '—'}</td>
                                        <td className="p-1">{(b.paid_at || '').slice(0, 10) || '—'}</td>
                                        <td className="p-1">{(b.stored_term_end || '').slice(0, 10) || '—'}</td>
                                        <td className="p-1">{(b.effective_term_end || '').slice(0, 10) || '—'}</td>
                                        <td className="p-1 text-white/50">{b.payment_type || '—'}{b.is_pro_rated ? ' · pro-rated' : ''}</td>
                                        <td className="p-1 text-right">£{Number(b.amount || 0).toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Mark as paid (manual) modal */}
      {markPaidModalOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          data-testid="mark-paid-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setMarkPaidModalOpen(false); }}
        >
          <div className="bg-[#0f0f0f] border border-cyan-500/40 rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Mark renewals as paid</h3>
            <p className="text-sm text-white/60 mb-4">
              Records {selectedIds.size} renewal(s) as paid <span className="text-cyan-300">outside the app</span> (cash, bank transfer, cheque…). The card is <span className="font-semibold">not</span> charged. New term bookings are created and the renewal disappears from this list.
            </p>
            <div className="space-y-2 max-h-48 overflow-auto mb-4 text-sm">
              {renewals.filter(r => selectedIds.has(r.id)).map(r => (
                <div key={r.id} className="flex justify-between text-white/80 border-b border-white/10 pb-1">
                  <span>{r.student_name}</span>
                  <span className="font-mono">£{(r.term_price || r.last_amount || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <label className="block text-sm text-white/70 mb-1">Note (optional)</label>
            <textarea
              value={markPaidNote}
              onChange={(e) => setMarkPaidNote(e.target.value)}
              placeholder="e.g. Cash collected at door 5 Jun, bank transfer ref 12345…"
              rows={3}
              className="w-full bg-[#1a1a1a] border border-[#333] rounded p-2 text-white text-sm"
              data-testid="mark-paid-note-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setMarkPaidModalOpen(false)}
                disabled={markingPaid}
                className="border-[#333] text-white/80 hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="mark-paid-confirm-btn"
              >
                {markingPaid ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Confirm mark paid
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenewalsTab;
